import { Button } from "@/components/ui/button";

import type { GridRow } from "./api";
import type { Programme } from "./discounts";
import { countryName, money, orders, pct1, projectionMissing, projectionWeighting } from "./format";
import { StrategyLines } from "./NewPriceBox";
import { ORDER_TYPE_COPY as T, programmeView, viewComparisons, viewPriceLine, type ViewKey, type ViewLookup } from "./order_views";

/** The Order type row (v26): Single order, Subscription, then one chip per active per-unit partner programme from the Discounts
 * read, by its name. A per-case programme prices case SKUs, which the Price Run does not list, so it is named, not offered. A
 * failed programmes read keeps the first two and says why. A chip changes nothing (AGENTS rule 11: `data-nonmutating`), so a
 * view-only person switches views too. */
export function OrderTypeRow({ view, programmes, unread, onChange }: {
  view: ViewKey | null; programmes: Programme[]; unread: boolean; onChange: (next: ViewKey | null) => void;
}) {
  const units = programmes.filter((p) => p.basis === "unit");
  const cases = programmes.filter((p) => p.basis === "case");
  const chips: Array<{ key: ViewKey | null; label: string }> = [
    { key: null, label: T.single }, { key: "subscription", label: T.subscription }, ...units.map((p) => ({ key: programmeView(p.id), label: p.name })),
  ];
  return (
    <div role="group" aria-label={T.label} className="flex flex-wrap items-center gap-1">
      <span className="mr-1 text-sm text-ks-grey-1">{T.label}</span>
      {chips.map((c) => (
        <Button key={c.key ?? "single"} size="sm" variant={c.key === view ? "default" : "outline"} aria-pressed={c.key === view} data-nonmutating onClick={() => onChange(c.key)}>
          {c.label}
        </Button>
      ))}
      {unread && <span className="text-xs text-ks-grey-1">{T.programmesUnread}</span>}
      {cases.length > 0 && <span className="text-xs text-ks-grey-1">{T.casesNote(cases.map((p) => p.name).join(", "))}</span>}
    </div>
  );
}

/** The drawer's view block, above the New price box: what the view's customer pays, the figures at that net (per ship country on
 * a store), the comparisons labelled against the list, and the verdicts at the net without a suggestion (C2 v1.23 ruling A6).
 * The detail under it stays the list price's. */
export function ViewDetail({ lookup, row }: { lookup: ViewLookup; row: GridRow }) {
  if (lookup.kind === "off") return null;
  const line = viewPriceLine(lookup);
  const f = lookup.kind === "row" ? lookup.row.figures : null;
  const cur = f?.price.currency ?? null;
  return (
    <section aria-label={lookup.kind === "pending" ? T.reading : T.detailTitle(lookup.view.view.label)} className="mt-3 rounded-md border border-ks-purple-3 p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-ks-purple">{lookup.kind === "pending" ? T.reading : T.detailTitle(lookup.view.view.label)}</div>
      {line && lookup.kind !== "pending" && <p className={line.muted ? "text-sm text-ks-grey-1" : "text-sm font-semibold text-ks-ink"}>{line.text}</p>}
      {f && lookup.kind === "row" && (
        <>
          {f.net_price && <p className="text-xs text-ks-grey-1">{T.exVat(money(f.net_price, cur))}</p>}
          <p className="text-sm text-ks-ink">{T.detailMargins(pct1(f.margins.gross_pct), pct1(f.margins.fulfilment_pct), pct1(f.margins.fees_pct))}</p>
          {row.channel === "shopify" && (
            <>
              {f.lines.map((l) => (
                <p key={l.location ?? "?"} className="text-xs text-ks-grey-1">{countryName(l.location)}: {T.detailMargins(pct1(l.margins.gross_pct), pct1(l.margins.fulfilment_pct), pct1(l.margins.fees_pct))} · {orders(l.orders)}</p>
              ))}
              <p className="text-xs text-ks-grey-1">{projectionWeighting(f.weighting)}</p>
            </>
          )}
          <p className="text-xs text-ks-grey-1">{viewComparisons(f, lookup.row.xero_sku)}</p>
          {f.missing.length > 0 && <p role="status" className="text-xs">{T.detailMissing(f.missing.map(projectionMissing).join(", "))}</p>}
          {lookup.row.strategy && <div className="text-xs text-ks-grey-1"><StrategyLines s={lookup.row.strategy} currency={cur} onUse={() => undefined} /></div>}
        </>
      )}
    </section>
  );
}
